(() => {
  'use strict';

  const DRAFT_KEY = 'ops-multilink:draft:v1';
  const REMOTE_KEY = 'ops-multilink:remote:v1';
  const SLUG_KEY = 'ops-multilink:slug:v1';

  const slugify = value => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);

  const parseJsonNode = (root, selector) => {
    try { return JSON.parse(root.querySelector(selector)?.textContent || '{}') || {}; }
    catch { return {}; }
  };

  const readJson = (key, fallback = null) => {
    try { return JSON.parse(localStorage.getItem(key) || 'null') ?? fallback; }
    catch { return fallback; }
  };

  const writeJson = (key, value) => {
    try { localStorage.setItem(key, JSON.stringify(value)); return true; }
    catch { return false; }
  };

  const requestJson = async (url, options = {}) => {
    const method = String(options.method || 'GET').toUpperCase();
    const response = await fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json', ...(options.headers || {}) },
      ...options
    });
    let data = {};
    try { data = await response.json(); } catch {}
    if (!response.ok || data.ok === false) {
      let pathname = String(url || '');
      try { pathname = new URL(url, location.origin).pathname; } catch {}
      const error = new Error(data.error || `Request failed (${response.status}) · ${method} ${pathname}`);
      error.status = response.status;
      error.data = data;
      error.requestMethod = method;
      error.requestPath = pathname;
      throw error;
    }
    return data;
  };

  const downloadBlob = (blob, filename) => {
    const href = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = href;
    anchor.download = filename;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(href), 1200);
  };

  const buildQr = (url, type = 'canvas', size = 520) => new QRCodeStyling({
    width: size,
    height: size,
    type,
    data: url,
    margin: Math.round(size * 0.035),
    qrOptions: { errorCorrectionLevel: 'H' },
    dotsOptions: { color: '#171717', type: 'rounded' },
    cornersSquareOptions: { color: '#171717', type: 'extra-rounded' },
    cornersDotOptions: { color: '#171717', type: 'dot' },
    backgroundOptions: { color: '#ffffff' }
  });

  const renderQr = (host, url) => {
    host.replaceChildren();
    if (!url || typeof QRCodeStyling === 'undefined') return;
    buildQr(url, 'canvas', 280).append(host);
  };

  const renderSvgBlob = async url => {
    const host = document.createElement('div');
    host.style.cssText = 'position:fixed;left:-10000px;top:-10000px;width:1px;height:1px;overflow:hidden';
    document.body.append(host);
    try {
      buildQr(url, 'svg', 1200).append(host);
      let svg = null;
      for (let attempt = 0; attempt < 30; attempt += 1) {
        svg = host.querySelector('svg');
        if (svg) break;
        await new Promise(resolve => setTimeout(resolve, 16));
      }
      if (!svg) throw new Error('QR SVG could not be created.');
      const clone = svg.cloneNode(true);
      clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      clone.setAttribute('width', '1200');
      clone.setAttribute('height', '1200');
      const xml = `<?xml version="1.0" encoding="UTF-8"?>\n${new XMLSerializer().serializeToString(clone)}`;
      return new Blob([xml], { type: 'image/svg+xml' });
    } finally {
      host.remove();
    }
  };

  const renderPngBlob = async url => {
    const host = document.createElement('div');
    host.style.cssText = 'position:fixed;left:-10000px;top:-10000px;width:1px;height:1px;overflow:hidden';
    document.body.append(host);
    try {
      buildQr(url, 'canvas', 1200).append(host);
      let canvas = null;
      for (let attempt = 0; attempt < 30; attempt += 1) {
        canvas = host.querySelector('canvas');
        if (canvas?.width) break;
        await new Promise(resolve => setTimeout(resolve, 16));
      }
      if (!canvas) throw new Error('QR PNG could not be created.');
      await new Promise(resolve => setTimeout(resolve, 50));
      const output = document.createElement('canvas');
      output.width = canvas.width;
      output.height = canvas.height;
      const context = output.getContext('2d', { alpha: false });
      if (!context) throw new Error('QR PNG could not be created.');
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, output.width, output.height);
      context.drawImage(canvas, 0, 0, output.width, output.height);
      return await new Promise((resolve, reject) => output.toBlob(blob => blob ? resolve(blob) : reject(new Error('QR PNG could not be created.')), 'image/png'));
    } finally {
      host.remove();
    }
  };

  function init(root) {
    if (root.dataset.opsMultilinkPublishReady === '1') return;
    root.dataset.opsMultilinkPublishReady = '1';

    const config = parseJsonNode(root, '[data-ops-multilink-config]');
    const apiPath = String(config.qrApiPath || root.dataset.opsQrApiPath || '/community/creators/api/qr').replace(/\/+$/, '');
    const labels = parseJsonNode(root, '[data-labels]');
    const publishBox = root.querySelector('.ops-publish');
    const publishButton = publishBox?.querySelector('button');
    const detailsPanel = root.querySelector('.ops-editor .ops-panel');
    const status = root.querySelector('.ops-status');
    if (!publishBox || !publishButton || !detailsPanel) return;

    let token = '';
    let remote = readJson(REMOTE_KEY, null);
    let slug = localStorage.getItem(SLUG_KEY) || '';
    let busy = false;
    let apiSignedIn = Boolean(config.customerLoggedIn);
    let authChecked = false;

    const notify = (message, error = false) => {
      if (!status) return;
      status.textContent = message || '';
      status.classList.toggle('error', error);
    };

    const signIn = () => {
      const returnTo = `${location.pathname}${location.search}`;
      const base = String(config.loginUrl || '/account/login');
      location.href = `${base}${base.includes('?') ? '&' : '?'}return_url=${encodeURIComponent(returnTo)}`;
    };

    const makeField = () => {
      const wrap = document.createElement('label');
      wrap.className = 'ops-field ops-multilink-slug';
      const title = document.createElement('span');
      title.textContent = labels.public_name || 'Public page name';
      const row = document.createElement('span');
      row.className = 'ops-slug-input';
      const prefix = document.createElement('span');
      prefix.textContent = 'qr.occultprintshop.com/l/';
      const input = document.createElement('input');
      input.type = 'text';
      input.maxLength = 64;
      input.autocapitalize = 'none';
      input.autocomplete = 'off';
      input.spellcheck = false;
      input.value = slug;
      input.setAttribute('aria-label', labels.public_name || 'Public page name');
      const help = document.createElement('small');
      help.textContent = labels.public_name_help || '';
      input.addEventListener('input', () => {
        const next = slugify(input.value);
        if (input.value !== next) input.value = next;
        slug = next;
        localStorage.setItem(SLUG_KEY, slug);
        refreshPublish();
      });
      row.append(prefix, input);
      wrap.append(title, row, help);
      return { wrap, input };
    };

    const campaignField = detailsPanel.querySelector('.ops-field');
    const slugField = makeField();
    campaignField?.insertAdjacentElement('afterend', slugField.wrap);

    const result = document.createElement('div');
    result.className = 'ops-multilink-published';
    result.hidden = true;
    const qrHost = document.createElement('div');
    qrHost.className = 'ops-multilink-qr';
    const permanent = document.createElement('code');
    permanent.className = 'ops-multilink-permanent';
    const pageLink = document.createElement('a');
    pageLink.target = '_blank';
    pageLink.rel = 'noopener';
    const actions = document.createElement('div');
    actions.className = 'ops-row-actions';
    const copyButton = document.createElement('button');
    copyButton.type = 'button';
    copyButton.textContent = labels.copy_qr || 'Copy QR link';
    const pngButton = document.createElement('button');
    pngButton.type = 'button';
    pngButton.textContent = labels.download_png || 'Download PNG';
    const svgButton = document.createElement('button');
    svgButton.type = 'button';
    svgButton.textContent = labels.download_svg || 'Download SVG';
    actions.append(copyButton, pngButton, svgButton);
    result.append(qrHost, permanent, pageLink, actions);
    publishBox.append(result);

    const currentDraft = () => {
      const draft = readJson(DRAFT_KEY, null);
      if (!draft || !window.OPSMultiLink) throw new Error(labels.invalid_draft || 'The current draft could not be read.');
      return window.OPSMultiLink.validate(draft);
    };

    const guessSlug = () => {
      if (slug) return;
      const draft = readJson(DRAFT_KEY, null);
      slug = slugify(draft?.page?.title || draft?.campaign_name || '');
      if (slug) {
        slugField.input.value = slug;
        localStorage.setItem(SLUG_KEY, slug);
      }
    };

    const setRemote = page => {
      if (!page?.campaign?.id) return;
      remote = {
        campaign_id: page.campaign.id,
        page_version: Number(page.version || 1),
        campaign_version: Number(page.campaign.version || 1),
        slug: page.slug,
        public_url: page.campaign.public_url,
        public_page_url: page.public_page_url,
        status: page.campaign.status
      };
      writeJson(REMOTE_KEY, remote);
      slug = page.slug || slug;
      localStorage.setItem(SLUG_KEY, slug);
      slugField.input.value = slug;
      if (page.document) writeJson(DRAFT_KEY, page.document);
      const nextUrl = new URL(location.href);
      nextUrl.searchParams.set('campaign', page.campaign.id);
      history.replaceState(null, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
      showPublished(page);
      refreshPublish();
    };

    const showPublished = page => {
      const campaign = page?.campaign || {};
      if (!campaign.public_url) return;
      result.hidden = false;
      permanent.textContent = campaign.public_url;
      pageLink.href = page.public_page_url || campaign.destination_url || '#';
      pageLink.textContent = labels.open_page || 'Open public page';
      renderQr(qrHost, campaign.public_url);
    };

    const setBusy = value => {
      busy = value;
      publishButton.disabled = value;
      copyButton.disabled = value;
      pngButton.disabled = value;
      svgButton.disabled = value;
    };

    const ensureToken = async () => {
      const data = await requestJson(apiPath);
      apiSignedIn = data.signed_in === true;
      authChecked = true;
      token = data.action_token || '';
      if (!apiSignedIn || !token) throw new Error(labels.auth_error || 'A fresh account token could not be created.');
      return token;
    };

    const checkAuth = async () => {
      try {
        const data = await requestJson(apiPath);
        apiSignedIn = data.signed_in === true && Boolean(data.action_token);
      } catch {
        apiSignedIn = false;
      } finally {
        authChecked = true;
        refreshPublish();
      }
      return apiSignedIn;
    };

    const publish = async () => {
      if (busy) return;
      if ((!authChecked || !apiSignedIn) && !(await checkAuth())) { signIn(); return; }
      try {
        guessSlug();
        const draft = currentDraft();
        if (!slug || slug.length < 2) throw new Error(labels.public_name_error || 'Choose a public page name with at least 2 characters.');
        setBusy(true);
        notify(remote?.campaign_id ? (labels.saving_remote || 'Saving campaign…') : (labels.publishing || 'Publishing campaign…'));
        const wasExisting = Boolean(remote?.campaign_id);
        const actionToken = await ensureToken();
        const path = remote?.campaign_id
          ? `${apiPath}/${encodeURIComponent(remote.campaign_id)}/multilink`
          : `${apiPath}/multilink`;
        const body = remote?.campaign_id
          ? { action_token: actionToken, slug, document: draft, page_version: remote.page_version, campaign_version: remote.campaign_version }
          : { action_token: actionToken, slug, document: draft };
        const data = await requestJson(path, { method: remote?.campaign_id ? 'PATCH' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        setRemote(data.page);
        notify(wasExisting ? (labels.saved_remote || 'Campaign saved. Your permanent QR has not changed.') : (labels.published || 'Published. Your permanent QR is ready.'));
        if (data.page?.document) {
          const reloadKey = `ops-multilink:render:${data.page.campaign.id}:${data.page.version}`;
          if (!sessionStorage.getItem(reloadKey)) {
            sessionStorage.setItem(reloadKey, '1');
            location.reload();
          }
        }
      } catch (error) {
        if (error.status === 401) { signIn(); return; }
        if (error.status === 409 && error.data?.page) setRemote(error.data.page);
        notify(error.message || labels.publish_error || 'The campaign could not be saved.', true);
      } finally {
        setBusy(false);
      }
    };

    const loadCampaign = async campaignId => {
      if (!campaignId) return;
      try {
        notify(labels.loading_campaign || 'Loading campaign…');
        const data = await requestJson(`${apiPath}/${encodeURIComponent(campaignId)}/multilink`);
        const page = data.page;
        const marker = `${page.campaign.id}:${page.version}:${page.campaign.version}`;
        const previous = sessionStorage.getItem('ops-multilink:last-loaded');
        setRemote(page);
        if (previous !== marker) {
          sessionStorage.setItem('ops-multilink:last-loaded', marker);
          location.reload();
          return;
        }
        notify(labels.loaded_campaign || 'Campaign loaded.');
      } catch (error) {
        if (error.status === 401) { signIn(); return; }
        notify(error.message || labels.load_error || 'The campaign could not be loaded.', true);
      }
    };

    const refreshPublish = () => {
      guessSlug();
      publishButton.disabled = busy || !authChecked;
      if (!authChecked) {
        publishButton.textContent = 'Checking account…';
      } else if (!apiSignedIn) {
        publishButton.textContent = labels.sign_in_publish || 'Sign in to publish';
      } else {
        publishButton.textContent = remote?.campaign_id ? (labels.save_changes || 'Save changes') : (labels.publish || 'Publish page');
      }
    };

    publishButton.addEventListener('click', publish);
    copyButton.addEventListener('click', async () => {
      if (!remote?.public_url) return;
      try { await navigator.clipboard.writeText(remote.public_url); notify(labels.copied_qr || 'Permanent QR link copied.'); }
      catch { notify(labels.copy_error || 'The permanent QR link could not be copied.', true); }
    });
    pngButton.addEventListener('click', async () => {
      if (!remote?.public_url) return;
      try { setBusy(true); downloadBlob(await renderPngBlob(remote.public_url), `ops-multilink-${slug || 'qr'}.png`); }
      catch (error) { notify(error.message, true); }
      finally { setBusy(false); refreshPublish(); }
    });
    svgButton.addEventListener('click', async () => {
      if (!remote?.public_url) return;
      try { setBusy(true); downloadBlob(await renderSvgBlob(remote.public_url), `ops-multilink-${slug || 'qr'}.svg`); }
      catch (error) { notify(error.message, true); }
      finally { setBusy(false); refreshPublish(); }
    });

    const params = new URLSearchParams(location.search);
    const campaignId = params.get('campaign');
    if (!campaignId && remote?.campaign_id) {
      remote = null;
      localStorage.removeItem(REMOTE_KEY);
    }
    if (remote?.campaign_id && remote.slug) {
      slug = remote.slug;
      slugField.input.value = slug;
      showPublished({ public_page_url: remote.public_page_url, campaign: { id: remote.campaign_id, public_url: remote.public_url, destination_url: remote.public_page_url, status: remote.status } });
    }
    refreshPublish();
    checkAuth().then(() => {
      if (campaignId && apiSignedIn) loadCampaign(campaignId);
    });
  }

  const boot = () => document.querySelectorAll('[data-ops-multilink]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();